import React, { ReactNode, useId } from 'react';
import styled from 'styled-components';

import { CsvImportBucketChip } from '@/features/csvImport/admin/atoms/CsvImportBucketChip';
import { CsvImportBucketVariant } from '@/features/csvImport/admin/types/csvImport';
import { Collapse } from '@/ui/Collapse';
import { Icon } from '@/ui/Icon';

const RootStyled = styled.section`
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.colors.neutral.outline1};
  border-radius: 8px;
  background-color: ${(p) => p.theme.colors.neutral.background};
`;

const HeaderStyled = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px 10px 8px;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary.outline};
    outline-offset: -2px;
  }
`;

const CHEVRON_HIT_AREA = 24;
const CHEVRON_GLYPH_SIZE = 10;

/**
 * The designed control is a 24px hit area around a small regular-weight chevron, measured
 * at roughly 5x10 in the design. The size has to come from font-size on a wrapper: Font
 * Awesome sizes its svg in em, and constraining the svg itself would stretch the glyph.
 */
const ChevronStyled = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${CHEVRON_HIT_AREA}px;
  height: ${CHEVRON_HIT_AREA}px;
  font-size: ${CHEVRON_GLYPH_SIZE}px;
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const ContentStyled = styled.div`
  border-top: 1px solid ${(p) => p.theme.colors.neutral.outline1};
`;

type CsvImportBucketSectionProps = {
  label: string;
  count: number;
  variant?: CsvImportBucketVariant;
  isExpanded: boolean;
  /** Omit to render a section that only ever shows its header. */
  onToggle?: VoidFunction;
  children?: ReactNode;
  className?: string;
};

export const CsvImportBucketSection = ({
  label,
  count,
  variant = 'neutral',
  isExpanded,
  onToggle,
  children,
  className,
}: CsvImportBucketSectionProps): JSX.Element => {
  const id = useId();
  const contentId = `${id}-content`;
  const isExpandable = !!onToggle;

  return (
    <RootStyled className={className}>
      <HeaderStyled
        type="button"
        onClick={onToggle}
        disabled={!isExpandable}
        aria-expanded={isExpandable ? isExpanded : undefined}
        aria-controls={isExpandable ? contentId : undefined}
      >
        <ChevronStyled aria-hidden>
          <Icon icon={isExpanded ? ['far', 'angle-down'] : ['far', 'angle-right']} />
        </ChevronStyled>
        <CsvImportBucketChip label={label} count={count} variant={variant} />
      </HeaderStyled>
      {isExpandable && (
        // The wrapper keeps aria-controls pointing at a real element: Collapse unmounts its
        // children while closed, so the id could not live on the content itself.
        <div id={contentId}>
          <Collapse isOpened={isExpanded}>
            <ContentStyled>{children}</ContentStyled>
          </Collapse>
        </div>
      )}
    </RootStyled>
  );
};
